import { useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
    isActivePromotion,
    useAssistantConfig,
    useBranchStaleness,
    useFleetOverview,
    usePromotions,
    useRefreshMutation,
    useTimeline,
    useTree,
    useTreeEdits,
} from "../api/queries";
import type { FleetOverview, Me, TreeResponse } from "../api/types";
import AssistantClustersTable from "../components/AssistantClustersTable";
import AssistantConfigTable from "../components/AssistantConfigTable";
import BranchStalenessTable from "../components/BranchStalenessTable";
import CoreTable from "../components/CoreTable";
import DataAgeBadge from "../components/DataAgeBadge";
import EnvGroupSection from "../components/EnvGroupSection";
import FleetToggle, { tabFleet, type Tab } from "../components/FleetToggle";
import IntegrationTable from "../components/IntegrationTable";
import PendingEditBanner from "../components/PendingEditBanner";
import PromotionTimeline from "../components/PromotionTimeline";
import PromotionTree from "../components/PromotionTree";
import Spinner from "../components/Spinner";
import SummaryCards from "../components/SummaryCards";
import UserMenu from "../components/UserMenu";
import { findEnvCluster, mergeOverviews } from "../util/argots";
import { ENV_LABELS, groupByEnv, worstStatus } from "../util/env";
import { oldestIso } from "../util/format";
import EnvDetailDrawer from "./EnvDetailDrawer";
import PromoteDialog from "./PromoteDialog";

type AssistantView = "namespaces" | "clusters";
const ASSISTANT_VIEWS: { view: AssistantView; label: string }[] = [
    { view: "namespaces", label: "Namespaces" },
    { view: "clusters", label: "Clusters" },
];

export default function Dashboard({ me }: { me: Me }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const params = useParams<{ envKey?: string }>();
    const tabParam = searchParams.get("fleet");
    const tab: Tab =
        tabParam === "argots" ||
        tabParam === "integration" ||
        tabParam === "timeline" ||
        tabParam === "branches" ||
        tabParam === "assistant"
            ? tabParam
            : "core";
    const isArgots = tab === "argots";
    const isTimeline = tab === "timeline";
    const isBranches = tab === "branches";
    const isAssistant = tab === "assistant";
    // Assistant tab sub-view: effective config per namespace (default) or the cluster-level
    // defaults per core cluster. Lives in the URL so a view can be linked.
    const assistantView: AssistantView = searchParams.get("view") === "clusters" ? "clusters" : "namespaces";
    const drawerEnv = params.envKey ?? null;

    const TIMELINE_DAYS = 7;
    const overviewQ = useFleetOverview(tabFleet(tab));
    const coreOverviewQ = useFleetOverview("core", isArgots);
    const treeQ = useTree(isArgots || drawerEnv !== null);
    const promotionsQ = usePromotions("active", isArgots || drawerEnv !== null);
    const editsQ = useTreeEdits(isArgots);
    const timelineQ = useTimeline(TIMELINE_DAYS, isTimeline);
    const branchesQ = useBranchStaleness(isBranches);
    const assistantQ = useAssistantConfig(isAssistant);
    const [promoteEnv, setPromoteEnv] = useState<string | null>(null);

    // Global refresh: force the active fleet's clusters plus the shared latest-tags
    // target due now. The scheduler picks them up on its next tick (POST returns 202),
    // and the 60s auto-refetch surfaces the new payloads. Promoter-gated like the
    // per-cluster button in EnvDetailDrawer.
    const refresh = useRefreshMutation();
    const refreshLabel = isBranches
        ? "Branches"
        : isAssistant
          ? "Assistant"
          : tab === "core"
            ? "Core"
            : tab === "integration"
              ? "Integration"
              : tab === "timeline"
                ? "Timeline"
                : "ArgoTS";
    const doRefresh = () => {
        if (isBranches) {
            void refresh.mutateAsync("branch-staleness");
            return;
        }
        if (isAssistant) {
            void refresh.mutateAsync("assistant");
            return;
        }
        void refresh.mutateAsync(`fleet:${tabFleet(tab)}`);
        void refresh.mutateAsync("tags");
    };

    const overview = overviewQ.data ?? null;
    const treeOverview = isArgots ? mergeOverviews(overview, coreOverviewQ.data ?? null) : overview;
    // Global data age: oldest payload fetchedAt across clusters, not network time.
    // The branches tab has its own single-payload age instead.
    const oldest = isBranches
        ? (branchesQ.data?.fetchedAt ?? null)
        : isAssistant
          ? (assistantQ.data?.fetchedAt ?? null)
          : overview && overview.clusters.length > 0
            ? oldestIso(overview.clusters.map((c) => c.fetchedAt))
            : null;

    const setTab = (t: Tab) => {
        const next = new URLSearchParams(searchParams);
        next.set("fleet", t);
        setSearchParams(next, { replace: true });
    };
    const setAssistantView = (v: AssistantView) => {
        const next = new URLSearchParams(searchParams);
        if (v === "namespaces") next.delete("view");
        else next.set("view", v);
        setSearchParams(next, { replace: true });
    };

    const edits = editsQ.data ?? [];
    const pendingEditEnvs = new Set(edits.filter((e) => e.state === "mr_open").flatMap((e) => e.affectedEnvs));

    return (
        <div className="app">
            <header className="header">
                <h1>Infra Versions</h1>
                <FleetToggle tab={tab} onChange={setTab} />
                <div className="header-right">
                    {me.role === "promoter" ? (
                        <button
                            className="btn"
                            disabled={refresh.isPending}
                            onClick={doRefresh}
                            title={`Re-fetch ${refreshLabel} cluster versions and latest tags from GitLab`}
                        >
                            {refresh.isPending ? `Refreshing ${refreshLabel}…` : `Refresh ${refreshLabel}`}
                        </button>
                    ) : null}
                    {oldest ? <DataAgeBadge fetchedAt={oldest} prefix="data" /> : null}
                    <UserMenu me={me} />
                </div>
            </header>
            <main>
                {!isBranches && !isAssistant && overviewQ.isPending ? <Spinner label="Loading cluster data…" /> : null}
                {!isBranches && !isAssistant && overviewQ.isError ? (
                    <div className="banner red">Failed to load overview: {overviewQ.error.message}</div>
                ) : null}
                {!isBranches && !isAssistant && overview ? <SummaryCards overview={overview} /> : null}

                {tab === "core" && overview
                    ? groupByEnv(overview.clusters).map(([env, clusters]) => (
                          <EnvGroupSection key={env} label={ENV_LABELS[env] ?? env} status={worstStatus(clusters)} count={clusters.length}>
                              <CoreTable clusters={clusters} latestTags={overview.latestTags} alerts={overview.alerts} />
                          </EnvGroupSection>
                      ))
                    : null}

                {tab === "integration" && overview
                    ? groupByEnv(overview.clusters).map(([env, clusters]) => (
                          <EnvGroupSection key={env} label={ENV_LABELS[env] ?? env} status={worstStatus(clusters)} count={clusters.length}>
                              <IntegrationTable clusters={clusters} latestTags={overview.latestTags} alerts={overview.alerts} />
                          </EnvGroupSection>
                      ))
                    : null}

                {isArgots ? (
                    <>
                        <PendingEditBanner edits={edits} />
                        <div className="section-head">
                            <h2>Promotion tree</h2>
                            {me.role === "promoter" ? (
                                <Link className="btn btn-small" to={{ pathname: "/tree/edit", search: location.search }}>
                                    Edit tree
                                </Link>
                            ) : null}
                        </div>
                        {treeQ.data ? (
                            <PromotionTree
                                tree={treeQ.data}
                                overview={treeOverview}
                                promotions={(promotionsQ.data ?? []).filter(isActivePromotion)}
                                pendingEditEnvs={pendingEditEnvs}
                                role={me.role}
                                onPromote={setPromoteEnv}
                            />
                        ) : treeQ.isError ? (
                            <div className="banner red">Failed to load promotion tree: {treeQ.error.message}</div>
                        ) : (
                            <Spinner label="Loading promotion tree…" />
                        )}
                        {treeOverview ? <UnmappedClusters overview={treeOverview} tree={treeQ.data ?? null} /> : null}
                    </>
                ) : null}

                {isTimeline ? (
                    <>
                        <div className="section-head">
                            <h2>Promotion timeline</h2>
                            <span className="muted">next {TIMELINE_DAYS} days — soak, deploy windows, predicted promotions</span>
                        </div>
                        {timelineQ.data ? (
                            <PromotionTimeline timeline={timelineQ.data} />
                        ) : timelineQ.isError ? (
                            <div className="banner red">Failed to load timeline: {timelineQ.error.message}</div>
                        ) : (
                            <Spinner label="Loading promotion timeline…" />
                        )}
                    </>
                ) : null}

                {isBranches ? (
                    <>
                        <div className="section-head">
                            <h2>Cluster branch staleness</h2>
                            <span className="muted">
                                core-aws-k8s-clusters per-cluster branches vs {branchesQ.data?.defaultBranch ?? "develop"}
                            </span>
                        </div>
                        {branchesQ.data ? (
                            <BranchStalenessTable branches={branchesQ.data.branches} defaultBranch={branchesQ.data.defaultBranch} />
                        ) : branchesQ.isError ? (
                            <div className="banner red">Failed to load branch staleness: {branchesQ.error.message}</div>
                        ) : (
                            <Spinner label="Loading branch staleness…" />
                        )}
                    </>
                ) : null}

                {isAssistant ? (
                    <>
                        <div className="section-head">
                            <h2>Assistant config</h2>
                            <div className="fleet-switch">
                                {ASSISTANT_VIEWS.map(({ view, label }) => (
                                    <button
                                        key={view}
                                        className={`btn btn-small ${assistantView === view ? "active" : ""}`}
                                        onClick={() => setAssistantView(view)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <span className="muted">
                                {assistantView === "clusters"
                                    ? "cluster-level genfx_assistant defaults per core cluster — chart defaults ← cluster values.yaml"
                                    : "effective genfx_assistant per cluster / namespace — chart defaults ← cluster values ← namespace values"}
                            </span>
                        </div>
                        {assistantQ.data ? (
                            assistantView === "clusters" ? (
                                <AssistantClustersTable clusters={assistantQ.data.clusters ?? []} />
                            ) : (
                                <AssistantConfigTable rows={assistantQ.data.rows} />
                            )
                        ) : assistantQ.isError ? (
                            <div className="banner red">Failed to load assistant config: {assistantQ.error.message}</div>
                        ) : (
                            <Spinner label="Loading assistant config…" />
                        )}
                    </>
                ) : null}
            </main>

            {drawerEnv !== null ? <EnvDetailDrawer key={drawerEnv} envKey={drawerEnv} me={me} /> : null}
            {promoteEnv !== null && treeQ.data && overview ? (
                <PromoteDialog
                    key={promoteEnv}
                    envKey={promoteEnv}
                    tree={treeQ.data}
                    overview={overview}
                    onClose={() => setPromoteEnv(null)}
                />
            ) : null}
        </div>
    );
}

function UnmappedClusters({ overview, tree }: { overview: FleetOverview; tree: TreeResponse | null }) {
    if (!tree?.config) return null;
    const mapped = new Set<string>();
    for (const [envKey, env] of Object.entries(tree.config.envs)) {
        const cluster = findEnvCluster(overview, envKey, env);
        if (cluster) mapped.add(cluster.cluster);
    }
    // Legacy (non-ArgoTS) core clusters are not promotable — only flag migrated ones.
    const promotable = (c: FleetOverview["clusters"][number]) => c.state.fleet === "argots" || !!c.state.argots;
    const unmapped = overview.clusters.filter((c) => promotable(c) && !mapped.has(c.cluster));
    if (unmapped.length === 0) return null;
    return <p className="muted footnote">Not in promotion tree: {unmapped.map((c) => c.cluster).join(", ")}</p>;
}
